import { useState, useEffect } from 'react';
import { isPushSupported, enableNotifications, disableNotifications, ensureSubscription } from '../notifications';

// Personal "turn on notifications" toggle — not a per-event prompt. Stays
// on until the user flips it off.
//
// Rendered in exactly ONE place: pages/Landing.jsx. It previously also sat in
// Sidebar.jsx (which put it in the sidebar of every page across all six
// domains) and in WarehouseHome.jsx; both were removed deliberately.
//
// Its initial state MUST come from localStorage's sf_user (kept live by
// persist() below), not the `user` prop — the prop is set once at login in
// App.js and never refreshed after a toggle, so reading it made the pill snap
// back to "off" whenever this component remounted, even though the real state
// was untouched. That still applies: navigating away from and back to the
// landing page remounts it.
function readPersistedEnabled(user) {
    try {
        const saved = JSON.parse(localStorage.getItem('sf_user') || 'null');
        if (saved && typeof saved.notifications_enabled === 'boolean') return saved.notifications_enabled;
    } catch (e) { /* fall through */ }
    return !!user?.notifications_enabled;
}

export default function NotificationBell({ user, compact = false }) {
    const [enabled, setEnabled]     = useState(() => readPersistedEnabled(user));
    const [busy, setBusy]           = useState(false);
    const [deniedMsg, setDeniedMsg] = useState(false);

    // Self-heal a dead subscription on every mount (portal load / revisit).
    // The toggle showing "on" only reflects portal_users.notifications_enabled
    // — it says nothing about whether THIS browser still has a live push
    // subscription registered, which can quietly go missing without ever
    // flipping that flag off. See notifications.js's ensureSubscription() doc.
    useEffect(() => {
        if (enabled && user?.username) ensureSubscription(user.username);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, user?.username]);

    if (!user?.username || !isPushSupported()) return null;

    function persist(next) {
        setEnabled(next);
        try {
            const saved = JSON.parse(localStorage.getItem('sf_user') || 'null');
            if (saved) {
                saved.notifications_enabled = next;
                localStorage.setItem('sf_user', JSON.stringify(saved));
            }
        } catch (e) { /* best-effort */ }
    }

    async function handleClick() {
        if (busy) return;
        setBusy(true);
        setDeniedMsg(false);

        if (enabled) {
            await disableNotifications(user.username);
            persist(false);
        } else {
            const result = await enableNotifications(user.username);
            if (result.success) {
                persist(true);
            } else if (result.reason === 'denied') {
                setDeniedMsg(true);
            }
        }
        setBusy(false);
    }

    return (
        <div style={compact ? S.wrapCompact : S.wrap}>
            <div
                onClick={handleClick}
                title={enabled ? 'Notifications on — click to turn off' : 'Turn on desktop notifications'}
                style={{ ...S.pill, ...(enabled ? S.pillOn : S.pillOff), opacity: busy ? 0.6 : 1 }}
            >
                <BellIcon on={enabled} />
                <span>{enabled ? 'Notifications on' : 'Turn on notifications'}</span>
            </div>
            {deniedMsg && (
                <div style={compact ? { ...S.deniedMsg, ...S.deniedMsgCompact } : S.deniedMsg}>
                    Notifications are blocked in your browser's site settings — enable them there, then try again.
                </div>
            )}
        </div>
    );
}

function BellIcon({ on }) {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            {!on && <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth="2" />}
        </svg>
    );
}

const S = {
    wrap: { padding: '0 16px 14px' },
    wrapCompact: { position: 'relative' },
    pill: {
        display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderRadius: 10,
        fontSize: 12.5, fontWeight: 600, cursor: 'pointer', transition: 'background .15s ease, color .15s ease',
    },
    pillOn:  { background: 'var(--acc-sidebar-active-bg, #EEE9F8)', color: 'var(--acc-sidebar-active-text, #4C3B78)' },
    pillOff: { background: 'var(--acc-sidebar-home-bg, #F6F2FB)', color: 'var(--acc-sidebar-home-text, #6E58A6)' },
    deniedMsg: {
        marginTop: 8, fontSize: 11, lineHeight: 1.4, color: '#9A7B3E',
        background: '#F5EDDC', padding: '8px 10px', borderRadius: 8,
    },
    deniedMsgCompact: {
        position: 'absolute', top: '100%', right: 0, marginTop: 6, width: 220, zIndex: 20,
        boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
    },
};
